<script setup>
  import BaseDialog from "@/base-components/Dialog/BaseDialog.vue";
  import Button from "@/base-components/Button";
  import { uri } from "@/constants/config";
  import InputText from "@/base-components/Form/InputText.vue";
  import { Form } from "vee-validate";
  import { onMounted, ref, computed, onBeforeUpdate } from "vue";
  import { useFetch } from "@/composables/useFetch";
  import { useI18n } from "vue-i18n";
  import Lucide from "@/base-components/Lucide";
  import { toast } from "vue3-toastify";
  import { FormLabel, FormSwitch } from "@/base-components/Form";
  import BaseInputSelect from "@/base-components/Form/BaseInputSelect.vue";
  import InputTextarea from "@/base-components/Form/InputTextarea.vue";
  import Multiselect from "vue-multiselect";
  import { preventDefault } from "tom-select/src/utils";
  import useValidate from "@/composables/useValidate.js";
  import { object } from "yup";

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    role: {
      type: String,
      default: "admin",
    },
    cloudList: {
      type: Array,
      default: [],
    },
    isEditting: {
      type: Boolean,
      default: false,
    },
  });
  const { t: $t } = useI18n();
  const emit = defineEmits([
    "close",
    "onSuccess",
    "fetchData",
    "registerDomain",
  ]);

  function close() {
    emit("close");
  }

  function onSuccess() {
    try {
    } catch (error) {
    } finally {
    }
    emit("onSuccess");
  }

  const loadingFlag = ref(false);
  const errorMessage = ref("");

  const successMessage = ref("");

  const form = ref({
    name: "",
    displayName: "",
    isReseller: false,
    isActive: true,
    config: {
      homePage: "",
      entryPoints: [""],
      appPage: "",
    },
  });
  const clearForm = () => {
    form.value = {
      name: "",
      displayName: "",
      isReseller: false,
      isActive: true,
      config: {
        homePage: "",
        entryPoints: [""],
        appPage: "",
      },
    };
  };

  const registerDomain = () => {
    emit("registerDomain", form.value);
  };

  const loadingFlagCloudDomainList = ref(false);
  const cloudSelected = ref(null);

  const addEntryPoint = (e) => {
    e.preventDefault();
    form.value.config.entryPoints.push("");
  };
  const removeEntryPoint = (index) => {
    form.value.config.entryPoints.splice(index, 1);
  };
  defineExpose({
    setDomainData,
  });
  function setDomainData(data) {
    form.value.name = data.name;
    form.value.displayName = data.displayName;
    form.value.isReseller = Boolean(data.isReseller);
    form.value.isActive = Boolean(data.isActive);
    form.value.config.homePage = data.config.homePage;
    form.value.config.entryPoints = data.config.entryPoints;
    form.value.config.appPage = data.config.appPage;
    if (data.isEditting) form.value.id = data.id;
  }
  const registerLabel = computed(() => {
    const title = props.isEditting
      ? $t("title.editDomain")
      : $t("title.addDomain");
    return title;
  });
  onBeforeUpdate(() => {
    if (!props.isEditting) clearForm();
  });

  const validationSchemaRegister = object(
    useValidate([
      "domain_name",
      "domain_display_name",
      "app_page",
      "home_page",
      "entry_point_0",
    ])
  );
</script>

<template>
  <section>
    <BaseDialog
      :size="'lg'"
      staticBackdrop
      :open="show"
      :has-footer="true"
      :title="registerLabel"
      @close="close"
    >
      <div class="rounded-md px-2 py-2 w-full max-h-[78vh] overflow-auto mb-5">
        <!-- FIXME: fix register function-->
        <Form
          class="w-full"
          id="registerForm"
          @submit="registerDomain"
          :validation-schema="validationSchemaRegister"
        >
          <div class="px-2">
            <div class="grid grid-cols-12 gap-4 mt-5 gap-y-5">
              <!-- Begin Name input -->
              <div
                class="col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-6 intro-y"
              >
                <InputText
                  name="domain_name"
                  :has-label="true"
                  :label="$t('label.domainName')"
                  v-model="form.name"
                  id="nameInput"
                  type="text"
                />
              </div>
              <!-- End Name input -->
              <!-- Begin displayName input -->
              <div
                class="col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-6 intro-y"
              >
                <InputText
                  name="domain_display_name"
                  :has-label="true"
                  :label="$t('label.domainDisplayName')"
                  v-model="form.displayName"
                  id="displayNameInput"
                  type="text"
                />
              </div>
              <!-- End displayName input -->
              <!-- Begin appPage input -->
              <div class="col-span-12 md:col-span-12 intro-y">
                <InputText
                  name="app_page"
                  :has-label="true"
                  :label="$t('label.appPage')"
                  v-model="form.config.appPage"
                  id="appPageInput"
                  type="text"
                />
              </div>
              <!-- End appPage input -->
              <!-- Begin homePage input -->
              <div class="col-span-12 md:col-span-12 intro-y">
                <InputText
                  name="home_page"
                  :has-label="true"
                  :label="$t('label.homePage')"
                  v-model="form.config.homePage"
                  id="homePageInput"
                  type="text"
                />
              </div>
              <!-- End homePage input -->
              <!-- Begin isReseller switch -->
              <div class="col-span-12 intro-y grid grid-cols-3 my-2">
                <!-- Begin isActive switch -->
                <div class="flex-wrap border-gray p-2 rounded-s-md border">
                  <span class="text-sm mb-1 mx-3">
                    {{ $t("label.isActive") }}
                  </span>
                  <FormSwitch class="flex flex-row items-start" dir="ltr">
                    <FormSwitch.Input
                      id="post-form-6"
                      type="checkbox"
                      class="xs"
                      v-model="form.isActive"
                    />
                  </FormSwitch>
                </div>
                <!-- End isActive switch -->

                <div class="flex-wrap border-gray p-2 rounded-s-md border">
                  <span class="text-sm mb-1 mx-3">
                    {{ $t("label.isReseller") }}
                  </span>
                  <FormSwitch class="flex flex-row items-start" dir="ltr">
                    <FormSwitch.Input
                      id="post-form-6"
                      type="checkbox"
                      class="xs"
                      v-model="form.isReseller"
                    />
                  </FormSwitch>
                </div>
              </div>
              <!-- End isReseller switch -->
              <!-- Begin entryPoints input -->
              <div class="col-span-12 md:col-span-12 intro-y">
                <div
                  v-for="(entryPoint, index) in form.config.entryPoints"
                  :key="index"
                >
                  <InputText
                    class="mt-6"
                    :name="'entry_point_' + index"
                    :has-label="true"
                    :label="$t('label.entryPoints') + ' ' + (index + 1)"
                    v-model="form.config.entryPoints[index]"
                    :id="'entry_point_' + index"
                    type="text"
                    :deleteButton="index === 0 ? false : true"
                    @deleteButtonClick="removeEntryPoint(index)"
                  />
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  id="addDomainButton"
                  class="w-36 mt-6"
                  @click="addEntryPoint"
                >
                  <Lucide icon="Plus" class="w-4 h-4 mr-2" />
                  {{ $t("label.addEntryPoint") }}
                </Button>
              </div>
              <!-- End entryPoints input -->
            </div>
          </div>
        </Form>
      </div>

      <template v-slot:footer>
        <free-style-shimmer
          width="100%"
          height="25px"
          class="w-full"
          v-if="loadingFlag"
        />
        <div class="flex col-md-12 items-end justify-end gap-2" v-else>
          <Button
            variant="outline-secondary"
            @click="close"
            size="sm"
            class="shadow-md justify-end items-end"
          >
            {{ $t("button.cancel") }}
          </Button>
          <Button
            variant="primary"
            size="sm"
            id="registerFormId"
            form="registerForm"
            class="shadow-md justify-end items-end"
            type="submit"
          >
            {{ registerLabel }}
          </Button>
        </div>
      </template>
    </BaseDialog>
  </section>
</template>

<style scoped></style>
